import type { WithActiveBranchId } from '@app/api/shared';
import type { IBaseQueryParams } from '@app/services/rtkQuery/baseQuery';
import type { NBranches, NEmployees, NFiles, NUsers } from '@app/types';

export enum UserEndpoints {
  CreateSecretLogin = 'CreateSecretLogin',
  GetEmployeePortfolio = 'GetEmployeePortfolio',
  AddEmployeePortfolio = 'AddEmployeePortfolio',
  DeleteEmployeePortfolio = 'DeleteEmployeePortfolio',
  UpdateEmployeePortfolio = 'UpdateEmployeePortfolio',
  GetUserById = 'GetUserById',
  SetUserActiveBranch = 'SetUserActiveBranch',
  GetUserProfileForView = 'GetUserProfileForView',
  GetUserProfileForEdit = 'GetUserProfileForEdit',
  GetUserSettings = 'GetUserSettings',
  UpdateUserSettings = 'UpdateUserSettings',
  PrepareEmailChange = 'PrepareEmailChange',
  VerifyEmailChange = 'VerifyEmailChange',
  UpdateUserPassword = 'UpdateUserPassword',
  UpdateUserProfile = 'UpdateUserProfile',
  SetUserPhoto = 'SetUserPhoto',
  NormalizeUserPhoto = 'NormalizeUserPhoto',
  UpdateUser = 'UpdateUser',
  UpdateEmail = 'UpdateEmail',
  GetUserNames = 'GetUserNames',
  SendoutInviteLetter = 'SendoutInviteLetter',
  ChangeUserPhoneNumber = 'ChangeUserPhoneNumber',
  UnarchiveUser = 'UnarchiveUser',
}

export interface ModelFile {
  Id: number;
  Name?: string | null;
  Data?: string | null;
  Lifetime?: string | null;
  Description?: string | null;
}

export interface ModelCreateSecretLogin {
  Secret: string;
  ValidTill: string;
  LoginUser: string;
  LoginUserEmail: string;
}

export type CreateSecretLoginRequest = {
  databaseId?: string;
  forUserId?: number;
} & Partial<WithActiveBranchId>;

export interface ModelEmployeePortfolio {
  Id: number;
  TimeCreated: string;
  Description: string | null;
  Order: number;
  UserId: number;
  CreateUserId: number;
  File: ModelFile | null;
}

export interface ModelEmployeePortfolioReport {
  Portfolios: ModelEmployeePortfolio[] | null;
}

export interface ModelEmployeePortfolioFileItem {
  Description: string | null;
  File: ModelFile;
}

export interface ModelEmployeePortfolioAdd {
  Description: string | null;
  UserId: number;
  Files: ModelEmployeePortfolioFileItem[] | null;
}

export interface ModelEmployeePortfolioDelete {
  UserId: number;
  PortfolioIds: number[] | null;
}

export interface ModelEmployeePortfolioUpdate {
  Id: number;
  UserId: number;
  Description: string | null;
  Order: number;
  File: ModelFile | null;
}

export type GetEmployeePortfolioRequest = WithActiveBranchId<{
  employeeId: number;
}>;

export type AddEmployeePortfolioRequest = WithActiveBranchId<{
  data: ModelEmployeePortfolioAdd;
}>;

export type DeleteEmployeePortfolioRequest = WithActiveBranchId<{
  data: ModelEmployeePortfolioDelete;
}>;

export type UpdateEmployeePortfolioRequest = WithActiveBranchId<{
  data: ModelEmployeePortfolioUpdate;
}>;

export type CreateSecretLoginResponse = ModelCreateSecretLogin;
export type GetEmployeePortfolioResponse = ModelEmployeePortfolioReport;
export type AddEmployeePortfolioResponse = ModelEmployeePortfolioReport;
export type DeleteEmployeePortfolioResponse = void;
export type UpdateEmployeePortfolioResponse = ModelEmployeePortfolio;

export type GetUserByIdRequest = WithActiveBranchId<{
  getUserId: number;
}>;

export type SetUserActiveBranchRequest = WithActiveBranchId<{
  branchId: number;
}>;

export type GetUserProfileForViewRequest = WithActiveBranchId<{
  profileUserId: number;
}>;

export type GetUserProfileForEditRequest = WithActiveBranchId<{
  logs?: IBaseQueryParams['logs'];
}>;

export type GetUserByIdResponse = NUsers.ModelUser;
export type SetUserActiveBranchResponse = NBranches.ModelSetUserActiveBranch;
export type GetUserProfileForViewResponse = NUsers.ModelUserProfile;
export type GetUserProfileForEditResponse = NUsers.ModelProfile;

export type GetUserSettingsRequest = WithActiveBranchId<{
  forUserId: number;
}>;

export type UpdateUserSettingsRequest = WithActiveBranchId<{
  data: NEmployees.ModelUserSettings;
}>;

export type PrepareEmailChangeRequest = WithActiveBranchId<{
  data: NUsers.ModelUserEmail;
}>;

export interface VerifyEmailChangeRequest {
  code: string;
}

export type UpdateUserPasswordRequest = WithActiveBranchId<{
  data: NUsers.ModelUserPassword;
}>;

export type UpdateUserProfileRequest = WithActiveBranchId<{
  data: NUsers.ModelProfile;
}>;

export interface SetUserPhotoPayload {
  UserId: number;
  PhotoBase64: string | null;
}

export type SetUserPhotoRequest = WithActiveBranchId<{
  data: SetUserPhotoPayload;
}>;

export type NormalizeUserPhotoRequest = WithActiveBranchId<{
  data: NFiles.ModelUserPhoto;
}>;

export type GetUserSettingsResponse = NEmployees.ModelUserSettings;
export type UpdateUserSettingsResponse = NEmployees.ModelUserSettings;
export type PrepareEmailChangeResponse = void;
export type VerifyEmailChangeResponse = { Email: string };
export type UpdateUserPasswordResponse = void;
export type UpdateUserProfileResponse = void;
export type SetUserPhotoResponse = NFiles.ModelPhoto;
export type NormalizeUserPhotoResponse = NFiles.ModelUserPhoto;

export type UpdateUserRequest = WithActiveBranchId<{
  data: NUsers.ModelUser;
  logs?: IBaseQueryParams['logs'];
}>;

export type UpdateEmailRequest = WithActiveBranchId<{
  userId: number;
  email: string;
}>;

export interface ModelUserNames {
  UserId: number;
  NameLast: string | null;
  NameFirst: string | null;
  NameMiddle: string | null;
  NameDative: string | null;
  NameGenitive: string | null;
}

export type GetUserNamesRequest = WithActiveBranchId<{
  forUserId: number;
}>;

export type SendoutInviteLetterRequest = WithActiveBranchId<{
  forUserId: number;
}>;

export type ChangeUserPhoneNumberRequest = WithActiveBranchId<{
  countryId: number;
  newNumber: string;
}>;

export type UnarchiveUserRequest = WithActiveBranchId<{
  userId: number;
}>;

export type UpdateUserResponse = NUsers.ModelUser;
export type UpdateEmailResponse = NUsers.ModelUser;
export type GetUserNamesResponse = ModelUserNames;
export type SendoutInviteLetterResponse = void;
export type ChangeUserPhoneNumberResponse = void;
